import { BusinessObjectBase } from './base';
import { activityAssignmentSchema } from '@schemas/business-objects/activity-assignment';
import type { Slot } from './slot';
import type { TermTutor } from './term-tutor';
import type { CalendarDate, Id } from '../base-types';
import type { Term } from './term';
import type { TermTime } from './term-time';
import type { Tutor } from './tutor';
import type { User } from './user';

export interface ActivityAssignmentData {
  readonly id: Id;
  readonly name?: string;
  readonly from?: CalendarDate;
  readonly to?: CalendarDate;
  readonly status?: string;
  readonly termTutorId: Id;
  readonly termTutor?: TermTutor;
  readonly slotId: Id;
  readonly slot?: Slot;
  readonly replacesId: Id;
  readonly replaces?: ActivityAssignment;
  readonly replacedBy?: ActivityAssignment;
}

export interface ActivityAssignment extends Readonly<ActivityAssignmentData> {}
type ActivityAssignmentCreateWithChildrenData = Partial<ActivityAssignmentData>;

export class ActivityAssignment extends BusinessObjectBase {
  static readonly entityName = 'ActivityAssignment' as const;
  static readonly plural = 'activity-assignments';
  static schema = activityAssignmentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ActivityAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ActivityAssignment(data));
  }

  static async findOne(filter?: any): Promise<ActivityAssignment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ActivityAssignment(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ActivityAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ActivityAssignment(data);
  }

  static async create(
    data: Omit<ActivityAssignmentData, 'id'>,
  ): Promise<ActivityAssignment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ActivityAssignment(result);
  }

  static async createMany(
    data: Omit<ActivityAssignmentData, 'id'>[],
  ): Promise<ActivityAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ActivityAssignment(item));
  }

  static async createWithChildren(
    data: ActivityAssignmentCreateWithChildrenData,
  ): Promise<ActivityAssignment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ActivityAssignment(result);
  }

  static async createManyWithChildren(
    data: ActivityAssignmentCreateWithChildrenData[],
  ): Promise<ActivityAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ActivityAssignment(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ActivityAssignmentData>,
  ): Promise<ActivityAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ActivityAssignment(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async checkExistingProposal(
    termTutorId: Id,
    slotId: Id | undefined,
    name: string,
    replacesId: Id | undefined,
  ): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/check-existing-proposal`;
    return this.post<any>(url, { termTutorId, slotId, name, replacesId });
  }
}
